import { Card, CardBody, Col, Row } from 'react-bootstrap';
import { Form, FormSpy } from 'react-final-form';
import { getLanguage, t } from 'react-switch-lang';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link, { ExternalLink } from '../../Links';
import SectionTitle from '../../SectionTitle';
import defaultStyles from '../../../styles/AccountDetails.module.scss';
import ottPayStyles from '../../../styles/OTTPayAccountDetails.module.scss';
import { events, logAmpEvent } from '../../../utils/Amplitude';
import Field from '../../InputFields';
import { formatAmountEn, formatAmountFr, formatPaymentAmount, getText, invalidFormAlert, mailcheckValidate, parseAmountStr, normalizeFormValues, formatAsTestId } from '../../../utils/Helpers';
import Button from '../../Button';
import { FIELD_VALIDATION_REGEX, FormatAmount, getDefaultMask, validationHelper } from '../../../utils/Validations';
import { DEFAULT_ACCOUNT_NAME_MAX_LENGTH, PAYMENT_AMOUNT_LIMITS } from '../../../utils/Constants';
import { DISPATCHERS, usePaymentProcessContext } from '../../../context/PaymentProcessContext';
import RenderMarkdown from '../../RenderMarkdown';
import { setScreenReaderAlert } from '../../../utils/Accessibility';
import ConfirmationModal from '../../ConfirmationModal';
import { RecipientLogo } from '../PaymentInformation';
import DebugInfo from '../../DebugInfo';
import { beginPayment } from '../../../utils/ApiClient';
import { isDevelopment } from '../../../utils/HostingEnv';
import MobileAppPopup from '../../MobileAppPopup';

const showToolTip = (labelObj, defaultLabelKey) => {
  if (labelObj?.Key) return labelObj.Key === defaultLabelKey;
  return false;
};

function PaymentAmountMaxAmountWarning({ styles }) {
  useEffect(() => {
    setScreenReaderAlert(t('AccountDetails_MaxAmount_Warning')
      .replace('xMaxLimit', FormatAmount(PAYMENT_AMOUNT_LIMITS.MAX)));
  }, []);
  return (
    <div className={styles.maxAmountWarningContainer}>
      {
        t('AccountDetails_MaxAmount_Warning')
          .replace('xMaxLimit', FormatAmount(PAYMENT_AMOUNT_LIMITS.MAX))
      }
      {' '}
      <ExternalLink
        href={t('Link_PaySimply_Help_TransactionLimits')}
        text={t('AccountDetails_MaxAmount_HelpCentre_LinkText')}
        ampEvent={events.USER_CLICKED_MAX_AMOUNT_LIMIT_HELP_LINK}
        aria-label={t('AccountDetails_MaxAmount_HelpCentre_LinkDescription')}
        testId={formatAsTestId('max amount limit helpcentre', 'external link')}
      />
    </div>
  );
}

function IntuitBanner({ styles }) {
  const {
    paymentProcess: {
      siteMode,
    },
  } = usePaymentProcessContext();
  const isIntuit = siteMode === 'Intuit';

  if (!isIntuit) return null;

  return (
    <aside className="card-header" id={styles.IntuitBanner}>
      <h1>{t('AccountDetails_IntuitBanner_Title')}</h1>
      <p>{t('AccountDetails_IntuitBanner_Body')}</p>
    </aside>
  );
}

export default function AccountDetails({
  autofill = null,
  autofillPennyFail = null,
  entryType = null,
  isHostedPage = false,
  hostedPagePayload = null,
}) {
  const ottPayBillers = isDevelopment ? [50954, 50992] : [62183]; // OTT Pay Biller Seq
  const isOTTPay = isHostedPage &&
    ottPayBillers.includes(hostedPagePayload?.PSBillServPartner_Seq ?? -1);
  const styles = isOTTPay ? ottPayStyles : defaultStyles;
  const {
    dispatchPaymentProcess,
    paymentProcess: {
      accountDetails,
      partner,
      isInvoice,
    },
  } = usePaymentProcessContext();
  const router = useRouter();
  const config = partner?.PSWebConfig;
  const fields = config?.Fields || [];
  const accountNameLabel = config?.AccountNameLabel;
  const accountNameMaxLength = config?.AccountNameMaxLength;
  const clientRefField = fields?.find((field) => field.DBField === 'ClientRef');
  const lang = getLanguage();
  const bannerText = lang === 'en' ? config?.BannerText?.En : config?.BannerText?.Fr;
  const [loading, setLoading] = useState(false);
  const [invalidEmailSuggestion, setInvalidEmailSuggestion] = useState(null);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const recipientName = getText(partner?.DisplayName);
  const emailAddressValidations = {
    MaxLength: 50,
    Regex: { regex: FIELD_VALIDATION_REGEX.VALID_EMAIL, customErrorMessage: t('Validation_Type_Email') },
  };
  const accountNameValidations = {
    MaxLength: accountNameMaxLength || DEFAULT_ACCOUNT_NAME_MAX_LENGTH,
    Regex: { regex: FIELD_VALIDATION_REGEX.VALID_CHAR, customErrorMessage: null },
    Type: 'AccountholderName',
  };

  const validate = (values) => {
    normalizeFormValues(values);
    const errors = {};

    errors.Email =
    validationHelper(emailAddressValidations, values.Email)?.errorMessage || undefined;

    if (!errors.Email && !showEmailConfirm) {
      const email = values.Email.toLowerCase().trim();
      const suggestedEmail = mailcheckValidate(email);
      setInvalidEmailSuggestion(suggestedEmail);
      if (suggestedEmail) {
        errors.Email = t('AccountDetails_MailCheck_EmailError');
      }
    } else {
      setInvalidEmailSuggestion(null);
    }

    errors.FullName =
    validationHelper(accountNameValidations, values.FullName)?.errorMessage || undefined;

    fields?.forEach((field) => {
      if (field.Type === 'Hidden') {
        // Skip validation for hidden fields
        errors[field.DBField] = undefined;
      } else {
        const validationResult = validationHelper(
          field.Validation,
          values[field.DBField],
          field.Mask
        );
        // If field name is not available, using the Label.
        const fieldName = getText(field.Name) || getText(field.Label);
        errors[field.DBField] = validationResult?.errorMessage?.replace('xField', fieldName) || undefined;
      }
    });

    const paymentAmountValidations = {
      MaxLength: 11,
      Amount: {
        MinPaymentAmount: PAYMENT_AMOUNT_LIMITS.MIN,
        MaxPaymentAmount: PAYMENT_AMOUNT_LIMITS.MAX,
      },
    };
    if (values.PaymentAmount) {
      const parsedAmount = parseAmountStr(values.PaymentAmount);
      errors.PaymentAmount =
        validationHelper(paymentAmountValidations, parsedAmount)?.errorMessage || undefined;
    } else {
      errors.PaymentAmount = t('Validation_Required');
    }

    return errors;
  };

  const onSubmit = async (values) => {
    normalizeFormValues(values);
    // eslint-disable-next-line no-param-reassign
    values.Email = values.Email.toLowerCase();

    const parsedAmount = parseAmountStr(values.PaymentAmount);
    // eslint-disable-next-line no-param-reassign
    values.PaymentAmount = parsedAmount;
    const accountDetailsValues = { ...values };
    // add hidden fields to the accountDetailsValues
    fields.forEach((field) => {
      if (field.Type !== 'Hidden') return;
      // if accountDetails already has a value for the hidden field,
      // it's likely passed in as a query param for Smart Payment Links, so repect that value
      // otherwise set the value as the default value
      const fieldValue = accountDetails?.[field.DBField] || field.Value;
      if (!fieldValue) return;
      accountDetailsValues[field.DBField] = fieldValue;
    });

    // if the user is on a hosted page, we immediately make the BeginPayment call
    if (isHostedPage && hostedPagePayload) {
      setLoading(true);
      const auth = router.query.jwt;

      const resp = await beginPayment(auth, {
        PartnerSeq: partner.PartnerSeq,
        PaymentOption: 'BillServ',
        Fee: '0.00', // no fee for hosted page, ignored on backend
        Fields: accountDetailsValues,
        Language: router.locale,
      });

      // handle error response
      if (resp.Result !== 0) {
        router.push({
          pathname: '/hosted-page/result',
          query: { Result: 'Error' },
        });
        return true;
      }

      const successQuery = {
        Result: 'Staged',
        BillerService_Seq: hostedPagePayload.BillerService_Seq,
        CustomerEmail: accountDetailsValues.Email,
        CustomerName: accountDetailsValues.FullName || '',
        PaymentAmount: accountDetailsValues.PaymentAmount,
      };

      // If the biller has an obscured ClientRef field (PSWebConfig.DBMask is not null),
      // it is considered sensitive information and we will not return this field.
      if (!fields.find((f) => f.DBField === 'ClientRef' && f.DBMask)) {
        successQuery.CustomerClientRef = accountDetailsValues.ClientRef || '';
      }

      router.push({
        pathname: '/hosted-page/result',
        query: successQuery,
      });
      return true;
    }

    dispatchPaymentProcess(DISPATCHERS.ENTER_ACCOUNT_DETAILS(accountDetailsValues));
    router.push(`${window.location.pathname}#payment-option`, undefined, { shallow: true });
    return true;
  };

  const handleGoBack = () => {
    const prevPath = router.query.landingPageKey ? `/lp/${router.query.landingPageKey}` : '/partner-search';
    router.push(prevPath);
  };

  const handleHostedPageCancel = () => {
    router.push({
      pathname: '/hosted-page/result',
      query: { Result: 'Cancelled' },
    });
  };

  useEffect(() => {
    if (isOTTPay) {
      const originalBg = document.body.style.backgroundColor;
      const container = document.querySelector('.container-xxl');
      const originalPadding = container?.style.padding;

      document.body.style.backgroundColor = '#F9F9F9';
      if (container) {
        container.style.padding = '0';
      }

      return () => {
        document.body.style.backgroundColor = originalBg;
        if (container) {
          container.style.padding = originalPadding || '';
        }
      };
    }
    return undefined;
  }, [isOTTPay]);

  useEffect(() => {
    if (!partner) return;
    const ampEventProps = {
      PartnerSeq: partner.PartnerSeq,
      'Recipient Name': partner.DisplayName.en,
    };
    if (entryType) ampEventProps['Entry Type'] = isHostedPage ? 'Biller Hosted Page' : entryType;
    logAmpEvent(events.USER_VIEWED_ACCOUNT_DETAILS_PAGE, ampEventProps);
  }, [partner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const preFilledFlag =
      accountDetails?.Email ||
      accountDetails?.FullName ||
      accountDetails?.ClientRef ||
      clientRefField?.Value ||
      accountDetails?.PaymentAmount ||
      fields?.some((field) => field?.DBField !== 'ClientRef' &&
        // check if the field is non-empty in accountDetails or if it has a default value
        (accountDetails?.[field.DBField] || field?.Value) &&
        field.Type !== 'Hidden');
    if (preFilledFlag) {
      setScreenReaderAlert(t('AccountDetails_PreFilled_ScreenReaderAlert'));
    }
  });

  function preprocessFullName(value) {
    return value?.replace(/\s+/g, ' ');
  }

  return (
    <>
      {isHostedPage || (
        <MobileAppPopup />
      )}
      <aside>
        <Card className={`${styles.paymentRecipientCardContainer} ${isHostedPage ? styles.hostedPageTitleContainer : ''}`}>
          <CardBody className={styles.paymentRecipientCard}>
            {isOTTPay ? (<h2>{t('PaymentInfo_RecipientTitle')}</h2>) : (<h1>{t('PaymentInfo_RecipientTitle')}</h1>)}
            <RecipientLogo partner={partner} />
            {partner?.LandingPageName && (
              <p>{getText(partner?.LandingPageName)}</p>
            )}
            <p className={styles.recipientName}>
              {recipientName}
            </p>
          </CardBody>
        </Card>
      </aside>
      <section>
        <Form
        // force form to re-render when partner changes
        // otherwise filled out inputs are not cleared when partner changes
        // such as when using the Revenu Québec link in the footer to change the partner
          key={partner?.PartnerSeq}
          onSubmit={onSubmit}
          validate={(values) => validate(values)}
          render={({
            handleSubmit,
            invalid,
            errors,
            form,
            values,
          }) => (
            <form
              onSubmit={(event) => {
                invalidFormAlert(invalid, errors);
                handleSubmit(event);
              }}
            >
              <FormSpy
                subscription={{ active: true, touched: true }}
                onChange={({ touched, active }) => {
                  if (touched.PaymentAmount && active !== 'PaymentAmount') {
                    if (!values.PaymentAmount) return;
                    const parsedAmount = parseAmountStr(values.PaymentAmount);
                    form.change('PaymentAmount', formatPaymentAmount(parsedAmount));
                  }
                }}
              />
              <Card className={styles.accountDetailsContainer}>
                <IntuitBanner styles={styles} />
                <CardBody>
                  <Row>
                    <Col md={12} className="d-flex flex-column justify-content-md-center">
                      {isOTTPay ? (
                        <div className={styles.hostedPageFormTitleContainer}>
                          {isOTTPay ? (
                            <h2>
                              {t('PaymentProcess_AccountDetails_Title')}
                            </h2>
                          ) : (
                            <h1>
                              {t('PaymentProcess_AccountDetails_Title')}
                            </h1>
                          )}
                          <p>
                            {t('PaymentProcess_AccountDetails_Subtitle')}
                          </p>
                        </div>
                      ) : (
                        <SectionTitle
                          title={t('PaymentProcess_AccountDetails_Title')}
                          subtitle={t('PaymentProcess_AccountDetails_Subtitle')}
                        />
                      )}
                    </Col>
                  </Row>
                  {bannerText && (
                  <Row className="justify-content-center">
                    <Col md={9} sm={12} className={styles.bannerTextContainer}>
                      <RenderMarkdown markdown={bannerText} explicitPTag />
                    </Col>
                  </Row>
                  )}
                  <Row className="justify-content-center">
                    <Col md={9} sm={12} className={styles.formContainer}>
                      <div className={styles.fieldContainer}>
                        <Field
                          className={styles.field}
                          inputGroupClassname={styles.fieldInputGroup}
                          label={t('PaymentProcess_AccountDetails_EmailLabel')}
                          id="Email"
                          name="Email"
                          disabled={isInvoice}
                          placeholder={t('PaymentProcess_AccountDetails_EmailLabel')}
                          initialValue={accountDetails?.Email}
                          mask={null}
                          change={form.change}
                          invalidEmailSuggestion={invalidEmailSuggestion}
                          setInvalidEmailSuggestion={setInvalidEmailSuggestion}
                          setShowEmailConfirm={setShowEmailConfirm}
                          showEmailConfirm={showEmailConfirm}
                          type="Email"
                          showClearButton={isOTTPay}
                        />
                      </div>
                      <div className={styles.fieldContainer}>
                        <Field
                          className={styles.field}
                          inputGroupClassname={styles.fieldInputGroup}
                          label={getText(accountNameLabel) || t('biller_accountHolderNameLabel')}
                          showToolTip={showToolTip(accountNameLabel, 'PayerNameField_Name')}
                          toolTipText={t('AccountDetails_AccountholderNameSynonyms')}
                          id="FullName"
                          name="FullName"
                          disabled={isInvoice}
                          autoComplete="on"
                          placeholder={getText(accountNameLabel)}
                          initialValue={preprocessFullName(accountDetails?.FullName)}
                          format={(value) => preprocessFullName(value)}
                          mask={null}
                          showClearButton={isOTTPay}
                        />
                      </div>
                      <div className={styles.fieldContainer}>
                        <Field
                          className={styles.field}
                          inputGroupClassname={styles.fieldInputGroup}
                          label={getText(clientRefField?.Label) || t('AccountNumField_Label')}
                          showToolTip={showToolTip(clientRefField?.Label, 'AccountNumField_Label')}
                          toolTipText={t('AccountDetails_AccountNumSynonyms')}
                          formatText={getText(clientRefField?.HelpText)}
                          id="ClientRef"
                          name="ClientRef"
                          disabled={isInvoice}
                          placeholder={getText(clientRefField?.Placeholder)}
                          initialValue={accountDetails?.ClientRef || clientRefField?.Value}
                          mask={getDefaultMask(clientRefField?.Mask, clientRefField?.Validation)}
                          change={form.change}
                          helpImage={clientRefField?.HelpImage}
                          helpImageAlt={
                        t('AccountDetails_Help_ImageDescription')
                          .replace('xField', getText(clientRefField?.Label))
                          .replace('xRecipient', recipientName)
                        }
                          showClearButton={isOTTPay}
                        />
                      </div>

                      {fields.map((field) => {
                        if (field.DBField === 'ClientRef') return null;
                        return (
                          <div className={styles.fieldContainer} key={field.DBField}>
                            <Field
                              className={`${styles.field} ${field.Type === 'Date' ? styles.dateField : ''}`}
                              inputGroupClassname={styles.fieldInputGroup}
                              label={getText(field.Label) || t(`${field.DBField}_DefaultLabel`)}
                              formatText={getText(field.HelpText)}
                              id={field.DBField}
                              name={field.DBField}
                              disabled={isInvoice}
                              placeholder={getText(field.Placeholder)}
                              initialValue={accountDetails?.[field.DBField] || field.Value}
                              type={field.Type}
                              mask={getDefaultMask(field.Mask, field.Validation)}
                              helpImage={field.HelpImage}
                              setValue={form.change}
                              showClearButton={isOTTPay}
                            />
                          </div>
                        );
                      })}
                      <div className={styles.fieldContainer}>
                        <Field
                          label={t('PaymentAmountField_Name')}
                          prefixItem={lang === 'en' && '$'}
                          suffixItem={lang === 'fr' && '$'}
                          format={lang === 'fr' ? formatAmountFr : formatAmountEn}
                          id="PaymentAmount"
                          name="PaymentAmount"
                          disabled={isInvoice}
                          inputMode="decimal"
                          className={styles.field}
                          inputGroupClassname={styles.fieldInputGroup}
                          placeholder={t('PaymentAmountField_Name')}
                          initialValue={formatPaymentAmount(accountDetails?.PaymentAmount)}
                          autoComplete="on"
                          errorAboveInput={
                          parseAmountStr(values.PaymentAmount) > PAYMENT_AMOUNT_LIMITS.MAX && (
                            <PaymentAmountMaxAmountWarning styles={styles} />
                          )
                        }
                          showClearButton={isOTTPay}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <p className={styles.footerPrivacyNotice}>{t('PaymentProcess_AccountDetails_PrivacyNotice')}</p>
                    {isHostedPage || (
                    <p className={styles.footerTermsAndCondition}>
                      {t('PaymentProcess_AccountDetails_TermsText')}
                      {' '}
                      <Link
                        href="/terms"
                        ampEvent={events.USER_CLICKED_ACCOUNT_DETAILS_TERMS}
                        testId={formatAsTestId('account details terms', 'link')}
                      >
                        {t('PaymentProcess_AccountDetails_TermsLinkText')}
                      </Link>
                    </p>
                    )}
                  </Row>
                </CardBody>
              </Card>
              <ConfirmationModal
                showModal={showConfirmationModal}
                onCancel={() => setShowConfirmationModal(false)}
                onConfirm={handleGoBack}
                ampEventPropsPageName="Payment Details"
                stepName={t('ProgressBarStep2')}
                returnFocusTo="backButton"
              />
              <div className={styles.bottomButtonWrapper}>
                <div className={styles.bottomButtonContainer}>
                  <div className={styles.buttonContainer}>
                    {isOTTPay || (
                    <Button
                      text={isHostedPage ? t('ReviewPage_Cancel_Button') : t('PaymentProcess_ButtonBack')}
                      ariaLabel={t('PaymentProcess_AccountDetails_ButtonBackDescription')}
                      ampEvent={isHostedPage ?
                        events.USER_CLICKED_CANCEL_BUTTON :
                        events.USER_CLICKED_BACK_BUTTON}
                      ampEventProps={{ Step: 'Account Details' }}
                      onClick={() => (isHostedPage ?
                        handleHostedPageCancel() :
                        setShowConfirmationModal(true))}
                      id="backButton"
                    />
                    )}
                    <Button
                      loading={loading}
                      text={isOTTPay ? t('PaymentProcess_ButtonNext') : t('PaymentProcess_ButtonContinue')}
                      ariaLabel={t('PaymentProcess_AccountDetails_ButtonContinueDescription')}
                      ampEvent={events.USER_CLICKED_CONTINUE_BUTTON}
                      ampEventProps={{ Step: 'Account Details' }}
                      type="submit"
                      testId={formatAsTestId('payment process continue', 'button')}
                    />
                  </div>
                  {!isOTTPay && (autofill || autofillPennyFail) && !isInvoice && (
                  <DebugInfo>
                    {autofill && (
                      <Button
                        debug
                        text="AUTOFILL DETAILS"
                        testId={formatAsTestId('autofill', 'button')}
                        onClick={() => Object.entries(autofill)
                          .forEach(([k, v]) => form.change(k, v))}
                      />
                    )}
                    {autofillPennyFail && (
                      <div className="mt-2">
                        <Button
                          debug
                          text="PENNYFAIL"
                          testId={formatAsTestId('pennyfail', 'button')}
                          onClick={() => Object.entries(autofillPennyFail)
                            .forEach(([k, v]) => form.change(k, v))}
                        />
                        <ExternalLink
                          href="https://developer.moneris.com/More/Testing/Penny%20Value%20Simulator"
                          text="Penny Values"
                          className="ms-2"
                          testId={formatAsTestId('penny values', 'external link')}
                        />
                      </div>
                    )}
                  </DebugInfo>
                  )}
                </div>
              </div>
            </form>

          )}
        />
      </section>
    </>
  );
}
